import type { Ref } from 'vue'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useDayjs } from '@shared/lib/format'

import { useApiTokensStore } from '../store'
import { useApiTokenCreateApi } from '../../../api/api-tokens/create.hook'
import { useApiTokensApi } from '../../../api/api-tokens/list.hook'

interface ModalCreateApiTokenReturn {
  refIsOpen: ComputedRef<boolean>
  refIsLoading: Ref<boolean>
  refName: Ref<string>
  refExpiresAt: Ref<Date | null>
  refMinExpiresDate: Date
  refCreatedToken: ComputedRef<string | null>
  refIsValid: ComputedRef<boolean>
  setName: (value: string | number | null) => void
  closeModal: () => void
  createToken: () => Promise<void>
}

export const useModalCreateApiToken = (): ModalCreateApiTokenReturn => {
  const store = useApiTokensStore()
  const dayjs = useDayjs()

  const refIsLoading = ref(false)
  const refName = ref('')
  const refExpiresAt = ref<Date | null>(null)
  const refMinExpiresDate = dayjs().toDate()

  const refIsOpen = computed<boolean>(() => store.isModalCreate)
  const refCreatedToken = computed<string | null>(() => store.createdPlainTextToken)
  const refIsValid = computed<boolean>(() => refName.value.trim().length > 0)

  const setName = (value: string | number | null): void => {
    refName.value = String(value ?? '')
  }

  const closeModal = (): void => {
    store.setModalCreate(false)
    store.setCreatedPlainTextToken(null)
    refName.value = ''
    refExpiresAt.value = null
  }

  const createToken = async (): Promise<void> => {
    if (!refIsValid.value || refIsLoading.value) return

    await useApiTokenCreateApi({
      name: refName.value.trim(),
      expiresAt: refExpiresAt.value ? dayjs(refExpiresAt.value).format(DateFormat.DefaultDate) : null,
      onStart: () => { refIsLoading.value = true },
      onSuccess: (data) => {
        store.setCreatedPlainTextToken(data.plain_text_token)
        void useApiTokensApi()
      },
      onFinish: () => { refIsLoading.value = false },
    })
  }

  return {
    refIsOpen,
    refIsLoading,
    refName,
    refExpiresAt,
    refMinExpiresDate,
    refCreatedToken,
    refIsValid,
    setName,
    closeModal,
    createToken,
  }
}
